import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import StageMasterApi from '../../../api/StageMasterApi';
import DataApi from '../../../api/DataApi';
import PicklistApi from '../../../api/PicklistApi';

const EMPTY_FORM = {
  name: '',
  default_probability: '',
  sequence_order: '',
};

const toStageOption = (value, label, sortOrder = 0) => ({
  value: String(value || ''),
  label: String(label || value || ''),
  sort_order: Number(sortOrder) || 0,
});

const extractStageOptions = (stageField) => {
  if (!stageField) return [];

  const source = Array.isArray(stageField.picklist_values_data)
    ? stageField.picklist_values_data
    : Array.isArray(stageField.picklist_values_json)
      ? stageField.picklist_values_json
      : [];

  return source
    .filter((pv) => pv && pv.is_active !== false)
    .map((pv) =>
      toStageOption(
        pv.key || pv.value_key || pv.value,
        pv.label || pv.value_label || pv.value,
        pv.sort_order
      )
    )
    .filter((opt) => opt.value)
    .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
};

const StageMasterManager = () => {
  const api = useMemo(() => new StageMasterApi(), []);
  const objectConfigApi = useMemo(() => new DataApi('objectconfig/opportunity'), []);
  const picklistApi = useMemo(() => new PicklistApi(), []);
  const [rows, setRows] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState({ text: '', type: null });

  const setError = (text) => setFeedback({ text, type: 'error' });
  const setSuccess = (text) => setFeedback({ text, type: 'success' });
  const clearFeedback = () => setFeedback({ text: '', type: null });
  const stageOptionsWithCurrent = useMemo(() => {
    const current = String(form.name || '').trim();
    if (!current) return stageOptions;
    const exists = stageOptions.some((option) => option.value === current);
    return exists ? stageOptions : [toStageOption(current, current), ...stageOptions];
  }, [form.name, stageOptions]);

  const loadData = async () => {
    const response = await api.fetchStages();
    const result = response?.data;
    if (result?.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadStageOptions = async () => {
      try {
        const response = await objectConfigApi.get('');
        const result = response?.data;
        const fields = result?.success ? result?.data?.fields || [] : [];
        const stageField = fields.find(
          (field) =>
            field?.field_name === 'stage' &&
            (field?.data_type === 'picklist' || field?.data_type === 'multipicklist')
        );

        const extracted = extractStageOptions(stageField);
        if (extracted.length > 0) {
          setStageOptions(extracted);
          return;
        }

        if (stageField?.id) {
          const picklistResponse = await picklistApi.getActivePicklistValues(stageField.id);
          const picklistData = picklistResponse?.data?.success ? picklistResponse?.data?.data || [] : [];
          const fallback = picklistData
            .map((pv) =>
              toStageOption(
                pv.value_key || pv.key || pv.value,
                pv.value_label || pv.label || pv.value,
                pv.sort_order
              )
            )
            .filter((opt) => opt.value)
            .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
          setStageOptions(fallback);
          return;
        }

        setStageOptions([]);
      } catch (error) {
        console.error('Failed to load opportunity stage options:', error);
        setStageOptions([]);
      }
    };

    loadStageOptions();
  }, [objectConfigApi, picklistApi]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      default_probability: item.default_probability ?? '',
      sequence_order: item.sequence_order ?? '',
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    clearFeedback();

    const payload = {
      name: String(form.name || '').trim(),
      default_probability: Number(form.default_probability),
      sequence_order: Number(form.sequence_order),
    };

    if (!payload.name) {
      setError('Stage name is required.');
      return;
    }
    if (Number.isNaN(payload.default_probability) || payload.default_probability < 0 || payload.default_probability > 100) {
      setError('Default probability must be between 0 and 100.');
      return;
    }
    if (!Number.isInteger(payload.sequence_order) || payload.sequence_order <= 0) {
      setError('Sequence order must be a positive integer.');
      return;
    }

    setIsSaving(true);
    try {
      const response = editingId
        ? await api.updateStage(editingId, payload)
        : await api.createStage(payload);
      const result = response?.data;
      if (result?.success) {
        setSuccess(editingId ? 'Stage updated successfully.' : 'Stage created successfully.');
        resetForm();
        await loadData();
      } else {
        setError(result?.message || 'Failed to save stage.');
      }
    } catch (error) {
      setError(error?.response?.data?.message || error?.message || 'Failed to save stage.');
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (id) => {
    clearFeedback();
    setIsSaving(true);
    try {
      const response = await api.deleteStage(id);
      const result = response?.data;
      if (result?.success) {
        if (editingId === id) {
          resetForm();
        }
        setSuccess('Stage deleted successfully.');
        await loadData();
      } else {
        setError(result?.message || 'Failed to delete stage.');
      }
    } catch (error) {
      setError(error?.response?.data?.message || error?.message || 'Failed to delete stage.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <h4 className="mb-3">Probability Manager</h4>
          <p className="text-muted mb-4">
            Manage stage and default probability mapping used in your opportunity pipeline.
          </p>

          <Form onSubmit={onSubmit}>
            <Row className="g-3">
              <Col md={5}>
                <Form.Label>Stage Name</Form.Label>
                <Form.Select
                  value={form.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">Select stage</option>
                  {stageOptionsWithCurrent.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Default Probability (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.default_probability}
                  onChange={(e) => onChange('default_probability', e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={2}>
                <Form.Label>Sequence Order</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  value={form.sequence_order}
                  onChange={(e) => onChange('sequence_order', e.target.value)}
                  disabled={isSaving}
                />
              </Col>
              <Col md={2} className="d-flex align-items-end gap-2">
                <Button type="submit" variant="primary" disabled={isSaving}>
                  {editingId ? 'Update' : 'Add'}
                </Button>
                {editingId ? (
                  <Button type="button" variant="secondary" onClick={resetForm} disabled={isSaving}>
                    Cancel
                  </Button>
                ) : null}
              </Col>
            </Row>
          </Form>

          {feedback.text ? (
            <div
              className={
                feedback.type === 'success' ? 'mt-3 text-success' : 'mt-3 text-danger'
              }
              role={feedback.type === 'success' ? 'status' : 'alert'}
            >
              {feedback.text}
            </div>
          ) : null}

          <div className="mt-4">
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Default Probability</th>
                  <th>Sequence</th>
                  <th style={{ width: 170 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      No stages found.
                    </td>
                  </tr>
                ) : (
                  rows.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{Number(item.default_probability).toFixed(2)}%</td>
                      <td>{item.sequence_order}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-primary" onClick={() => onEdit(item)} disabled={isSaving}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => onDelete(item.id)}
                            disabled={isSaving}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StageMasterManager;
